/**
 * Pure query-shape helpers for the Algolia-backed brand LISTING.
 *
 * Sibling to `brandSearchQuery.ts` (which serves the pickers) and
 * `referenceListQuery.ts` (which serves the reference listing). Kept out of
 * `algolia.ts` so the parameter building and the hit → document mapping can be
 * unit-tested without pulling in the Algolia client — and the `import.meta.env`
 * credential read that happens at its module scope.
 */

import { Timestamp } from 'firebase/firestore';
import type { BrandStatus, WatchBrand } from '../types';

/**
 * Attributes the brand listing renders per row.
 *
 * The first ten are what the pickers already asked for; the last five landed
 * with watchlock #863, which is what made this listing expressible in Algolia at
 * all — before it, the Refs column, both date sorts and the status pills all
 * read fields the index did not carry, so the page had to load all 561 brand
 * documents from Firestore on mount.
 *
 * Requesting an attribute the index doesn't hold is silently dropped rather than
 * an error, so this list is only as good as the indexer it mirrors (watchlock).
 */
export const BRAND_LIST_ATTRIBUTES = [
  'objectID',
  'name',
  'displayName',
  'country',
  'parentOrg',
  // The FK. The listing resolves the owner through this first; the legacy
  // string above is only a fallback for brands that were never linked.
  'parentOrgId',
  'founded',
  'hasLogo',
  'isDisabled',
  'isPromoted',
  'isMicroBrand',
  'status',
  'needsReview',
  'parentReferenceCount',
  'createdAt',
  'lastImportedAt',
] as const;

/**
 * Fields the listing renders that the index does NOT carry, and which therefore
 * come from a Firestore read of the individual document.
 *
 * Documented rather than merely implied, because each one is a column or badge
 * that would silently go blank if the hydration pass were dropped:
 *
 * - `mainWebsite` — the Website column.
 * - `acquisitions` — the ⚘ Acquisitions badge, and the "N brands list this as an
 *   acquisition" warning in the editor.
 * - `yearCeased` — a CSV export column.
 * - `shopifyEnabled` / `shopifyUrl` — the legacy fallback inside
 *   `brandHasShopifySource`, used only for brands with no import-sources doc.
 *
 * Plus every field the edit form writes back (description, information, logo,
 * hero image, glyph, winding directions, …) — which is why the editor always
 * opens on a freshly read document rather than on a listing row.
 */
export const BRAND_FIELDS_NOT_INDEXED = [
  'mainWebsite',
  'acquisitions',
  'yearCeased',
  'shopifyEnabled',
  'shopifyUrl',
] as const;

/** One `watchkeeper_brands` record, as the listing consumes it. */
export interface BrandListingHit {
  objectID: string;
  name?: string;
  displayName?: string;
  country?: string;
  parentOrg?: string;
  parentOrgId?: string;
  founded?: number;
  hasLogo?: boolean;
  isDisabled?: boolean;
  isPromoted?: boolean;
  isMicroBrand?: boolean;
  status?: string;
  needsReview?: boolean;
  parentReferenceCount?: number;
  /** Epoch milliseconds — the indexer flattens Firestore Timestamps. */
  createdAt?: number;
  /** Epoch milliseconds. Absent on brands that have never been imported. */
  lastImportedAt?: number;
}

/** Every `BrandStatus` the editor's status control offers. */
const BRAND_STATUSES: readonly string[] = [
  'active',
  'defunct',
  'dormant',
  'revived',
  'absorbed',
];

/**
 * Algolia search parameters for one page of the brand listing.
 *
 * No `facets`: this listing holds every matching record locally (see
 * `fetchBrandListing`), so the continent / country / parent-org pill counts are
 * computed over that set — which is disjunctive by construction. Picking France
 * narrows the ROWS without collapsing the country pills, because the counts are
 * derived from the unfiltered list rather than from the filtered response. Facet
 * counts would have to be re-queried once per dimension, each excluding its own
 * selection, to match that.
 *
 * No `facetFilters` either, for the same reason: filtering stays local, so a
 * filter change costs no round trip at all.
 *
 * `attributesToHighlight: []` because no listing surface renders highlight
 * markup. `analytics: false` keeps admin browsing out of the app's search
 * analytics. `cacheable: false` bypasses the SDK's in-memory response cache, so
 * reopening the tab after editing a brand shows the edit rather than replaying
 * the pre-edit response.
 */
export function buildBrandListParams(
  page: number,
  hitsPerPage: number,
): Record<string, unknown> {
  return {
    hitsPerPage,
    page,
    analytics: false,
    cacheable: false,
    attributesToRetrieve: [...BRAND_LIST_ATTRIBUTES],
    attributesToHighlight: [],
  };
}

/**
 * Map one Algolia hit onto the `WatchBrand` shape the listing already renders.
 *
 * Reconstructing `Timestamp`s from the indexed epoch milliseconds is what keeps
 * the rest of the page untouched: `formatRelativeTime`, the Added column's
 * `.toDate()` and both date sorts all keep working on a hit-derived row exactly
 * as they did on a Firestore document.
 *
 * Absent numeric fields stay ABSENT rather than becoming 0 — `parentReferenceCount`
 * of `undefined` means "not computed yet" everywhere on this page (it is what
 * makes the row badge render "…" and keeps the Delete affordance hidden), so
 * coercing it would invent a count the server never wrote.
 *
 * `status` is validated against the known set rather than cast: the field is a
 * free-form string in the index, and an unrecognised value would otherwise flow
 * into a `BrandStatus`-typed slot and out to the status badge.
 */
export function brandHitToWatchBrand(hit: BrandListingHit): WatchBrand {
  const brand: WatchBrand = {
    id: hit.objectID,
    name: hit.name ?? hit.displayName ?? hit.objectID,
  };
  if (hit.displayName !== undefined) brand.displayName = hit.displayName;
  if (hit.country !== undefined) brand.country = hit.country;
  if (hit.parentOrg !== undefined) brand.parentOrg = hit.parentOrg;
  // Empty string means "no link" on the index; leave the field off rather than
  // carry a foreign key to nothing.
  if (hit.parentOrgId) brand.parentOrgId = hit.parentOrgId;
  if (hit.founded !== undefined) brand.founded = hit.founded;
  if (hit.isDisabled !== undefined) brand.isDisabled = hit.isDisabled;
  if (hit.isPromoted !== undefined) brand.isPromoted = hit.isPromoted;
  if (hit.isMicroBrand !== undefined) brand.isMicroBrand = hit.isMicroBrand;
  if (hit.needsReview !== undefined) brand.needsReview = hit.needsReview;
  if (hit.status !== undefined && BRAND_STATUSES.includes(hit.status)) {
    brand.status = hit.status as BrandStatus;
  }
  if (typeof hit.parentReferenceCount === 'number') {
    brand.parentReferenceCount = hit.parentReferenceCount;
  }
  if (typeof hit.createdAt === 'number') {
    brand.createdAt = Timestamp.fromMillis(hit.createdAt);
  }
  if (typeof hit.lastImportedAt === 'number') {
    brand.lastImportedAt = Timestamp.fromMillis(hit.lastImportedAt);
  }
  return brand;
}

/**
 * Overlay freshly read Firestore documents onto the listing.
 *
 * A document REPLACES its listing row outright rather than being merged field by
 * field: the document is authoritative and complete, whereas the row is a
 * projection, so a field-level merge would let a value the admin has just
 * cleared survive as the stale indexed one.
 *
 * Documents with no matching row are APPENDED. That is not a corner case — it is
 * how disabled brands stay visible at all, since the indexer evicts them from
 * `watchkeeper_brands` entirely. Order is otherwise preserved, so the caller's
 * sort is unaffected.
 *
 * `docs` is de-duplicated by id, last one winning. The caller assembles it from
 * several targeted queries that legitimately overlap — a disabled brand can also
 * be one carrying acquisitions — and an id appended twice would show as two
 * rows for the same brand.
 */
export function mergeBrandDocs(
  listing: WatchBrand[],
  docs: WatchBrand[],
): WatchBrand[] {
  if (docs.length === 0) return listing;
  const byId = new Map(docs.map((d) => [d.id, d]));
  const merged = listing.map((row) => byId.get(row.id) ?? row);
  const seen = new Set(listing.map((row) => row.id));
  for (const [id, doc] of byId) {
    if (!seen.has(id)) merged.push(doc);
  }
  return merged;
}

/**
 * Statuses that can carry a `yearCeased`, which the CSV export has a column for
 * and the index does not hold.
 *
 * The export hydrates only the rows whose status makes the field meaningful —
 * ~13 brands rather than all 561 — so a "Download CSV" click stays cheap while
 * the column stays correct. An `active` brand with a stray `yearCeased` would
 * export blank, which is what the editor already implies: the field is only
 * shown for ceased statuses.
 */
export const CEASED_BRAND_STATUSES: readonly string[] = [
  'defunct',
  'dormant',
  'absorbed',
];
